import { AxiosError } from "axios";
import { handleApiError } from "./instanceApi";
import { moodleInstanceApi } from "./moodleInstanceApi";
import { toast } from "react-toastify";

// Centralized error handler for BigBlueButton operations
const handleBBBError = (
  _operation: string,
  error: any,
  showToast: boolean = true
): any => {
  let userMessage = "Đã xảy ra lỗi không xác định.";
  let errorCode = "UNKNOWN_ERROR";

  if (error?.response?.status === 401) {
    userMessage = "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.";
    errorCode = "AUTH_EXPIRED";
  } else if (error?.response?.status === 403) {
    userMessage =
      "Bạn không có quyền truy cập lớp học này. Vui lòng liên hệ giảng viên.";
    errorCode = "ACCESS_DENIED";
  } else if (error?.response?.status === 404) {
    userMessage = "Không tìm thấy lớp học này. Có thể lớp học đã bị xóa.";
    errorCode = "NOT_FOUND";
  } else if (
    error?.message?.includes("network") ||
    error?.code === "NETWORK_ERROR"
  ) {
    userMessage = "Lỗi kết nối mạng. Vui lòng kiểm tra kết nối internet.";
    errorCode = "NETWORK_ERROR";
  } else if (
    error?.message?.includes("enrol") ||
    error?.message?.includes("permission")
  ) {
    userMessage =
      "Bạn chưa đăng ký khóa học này. Vui lòng đăng ký trước khi tham gia.";
    errorCode = "NOT_ENROLLED";
  } else if (error?.message) {
    userMessage = `Lỗi: ${error.message}`;
    errorCode = "API_ERROR";
  }

  if (showToast) {
    toast.error(userMessage);
  }

  return {
    success: false,
    error: errorCode,
    message: userMessage,
  };
};

/**
 * Check if the current user is authenticated with Moodle
 */
export async function checkMoodleAuth() {
  try {
    const { data } = await moodleInstanceApi.get(
      "/webservice/rest/server.php",
      {
        params: { wsfunction: "core_webservice_get_site_info" },
      }
    );
    return { success: true, data };
  } catch (error) {
    const errorResult = handleBBBError("Moodle Auth Check", error, false);
    return errorResult;
  }
}

/**
 * Fetch BigBlueButton activities for a given course
 */
export async function getBigBlueButtonActivities(courseId: number) {
  try {
    const { data } = await moodleInstanceApi.get(
      "/webservice/rest/server.php",
      {
        params: {
          wsfunction: "mod_bigbluebuttonbn_get_bigbluebuttonbns_by_courses",
          "courseids[0]": courseId,
        },
      }
    );
    const activities = data?.bigbluebuttonbns || [];
    return { success: true, data: activities };
  } catch (error) {
    const errorResult = handleBBBError(
      `Get BBB Activities for course ${courseId}`,
      error,
      false
    );
    return { ...errorResult, data: [] };
  }
}

/**
 * Retrieve detailed meeting info for a BigBlueButton activity
 */
export async function getBigBlueButtonMeetingInfo(bbbId: number): Promise<any> {
  try {
    const { data } = await moodleInstanceApi.get(
      "/webservice/rest/server.php",
      {
        params: {
          wsfunction: "mod_bigbluebuttonbn_get_bigbluebuttonbn_info",
          bigbluebuttonbnid: bbbId,
        },
      }
    );

    // nếu Moodle trả về exception
    if ((data as any).exception) {
      throw new Error((data as any).message || (data as any).exception);
    }

    return data;
  } catch (err: any) {
    return null;
  }
}

/**
 * Generate the join URL for a BigBlueButton meeting
 * This uses Moodle's web service directly and returns the URL string
 */
export async function updateBBBcompletion(bbbId: number): Promise<string> {
  try {
    const response = await moodleInstanceApi.get(
      "/webservice/rest/server.php",
      {
        params: {
          wsfunction: "mod_bigbluebuttonbn_view_bigbluebuttonbn",
          bigbluebuttonbnid: bbbId,
        },
      }
    );
    return response?.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
}

/**
 * Get BigBlueButton activity info (different from meeting info)
 * This uses mod_bigbluebuttonbn_get_bigbluebuttonbn_info which returns activity configuration
 */
export async function getBigBlueButtonActivityInfo(bbbId: number) {
  try {
    const { data } = await moodleInstanceApi.get(
      "/webservice/rest/server.php",
      {
        params: {
          wsfunction: "mod_bigbluebuttonbn_get_bigbluebuttonbn_info",
          bigbluebuttonbnid: bbbId,
        },
      }
    );

    // Validate response structure
    if (typeof data === "string") {
      throw new Error(`Invalid response format: ${data}`);
    }

    return { success: true, data };
  } catch (error) {
    const errorResult = handleBBBError(
      `Get BBB Activity Info for ID ${bbbId}`,
      error,
      false
    );
    return { ...errorResult, data: null };
  }
}

/**
 * Check if user can join BigBlueButton meeting using the proper API
 * This is the correct way to check meeting status!
 */
export async function checkBigBlueButtonCanJoin(courseModuleId: number) {
  try {
    const { data } = await moodleInstanceApi.get(
      "/webservice/rest/server.php",
      {
        params: {
          wsfunction: "mod_bigbluebuttonbn_can_join",
          cmid: courseModuleId,
        },
      }
    );

    // Validate response structure
    if (typeof data === "string") {
      throw new Error(`Invalid response format: ${data}`);
    }

    return {
      success: true,
      data: {
        canJoin: Boolean(data.can_join),
        message: data.message || "Không có thông tin trạng thái",
        courseModuleId: data.cmid,
      },
    };
  } catch (error) {
    const errorResult = handleBBBError(
      `Check Can Join for course module ${courseModuleId}`,
      error,
      false
    );
    return {
      ...errorResult,
      data: {
        canJoin: false,
        message: "Không thể kiểm tra trạng thái",
        courseModuleId,
      },
    };
  }
}

/**
 * Get user's enrolled courses
 */
export async function getUserEnrolledCourses() {
  try {
    const { data } = await moodleInstanceApi.get(
      "/webservice/rest/server.php",
      {
        params: {
          wsfunction: "core_enrol_get_users_courses",
          userid: 0, // 0 means current user
        },
      }
    );

    // Validate response
    if (typeof data === "string") {
      throw new Error(`Invalid response format: ${data}`);
    }

    const courses = Array.isArray(data) ? data : [];
    return { success: true, data: courses };
  } catch (error) {
    const errorResult = handleBBBError(
      "Get User Enrolled Courses",
      error,
      false
    );
    return { ...errorResult, data: [] };
  }
}

/**
 * Find course module ID for a BigBlueButton activity with smart fallback
 */
export async function findCourseModuleIdForBBB(
  bigBlueButtonId: number
): Promise<number | null> {
  try {
    // Strategy 1: Try enrolled courses API
    const coursesResult = await getUserEnrolledCourses();

    if (
      coursesResult.success &&
      coursesResult.data &&
      coursesResult.data.length > 0
    ) {
      for (const course of coursesResult.data) {
        try {
          const activitiesResult = await getBigBlueButtonActivities(course.id);

          if (
            activitiesResult.success &&
            activitiesResult.data &&
            activitiesResult.data.length > 0
          ) {
            const activity = activitiesResult.data.find(
              (a: any) => a.id === bigBlueButtonId
            );
            if (activity) {
              return activity.coursemodule;
            }
          }
        } catch (courseError) {
          // Continue to next course
        }
      }
    }

    // Strategy 2: Smart fallback - try common course IDs
    const commonCourseIds = [292, 293, 294, 295, 296, 297, 298, 299, 300];

    for (const courseId of commonCourseIds) {
      try {
        const activitiesResult = await getBigBlueButtonActivities(courseId);

        if (
          activitiesResult.success &&
          activitiesResult.data &&
          activitiesResult.data.length > 0
        ) {
          const activity = activitiesResult.data.find(
            (a: any) => a.id === bigBlueButtonId
          );
          if (activity) {
            return activity.coursemodule;
          }
        }
      } catch (courseError) {
        // Continue to next course
      }
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Construct proper BigBlueButton join URL with fallback for access denied users
 */
export async function constructBigBlueButtonJoinUrl(
  bigBlueButtonId: number
): Promise<string> {
  const moodleBaseUrl = process.env.REACT_APP_MOODLE_API?.replace(
    "/webservice/rest/server.php",
    ""
  );

  if (!moodleBaseUrl) {
    throw new Error("Moodle base URL not configured");
  }

  // Try to find the correct course module ID
  const courseModuleId = await findCourseModuleIdForBBB(bigBlueButtonId);

  // Get token from environment or current session
  const token =
    process.env.REACT_APP_MOODLE_TOKEN ||
    localStorage.getItem("moodle_token") ||
    "cf8d63a864797fed69f052f633c07d2e"; // Fallback token

  if (courseModuleId) {
    // Use the proper format: id=courseModuleId&bn=bigBlueButtonId&wstoken=token
    const url = `${moodleBaseUrl}/mod/bigbluebuttonbn/bbb_view.php?action=join&id=${courseModuleId}&bn=${bigBlueButtonId}&wstoken=${token}`;
    return url;
  } else {
    // Fallback: Thử với BBB ID khi không tìm thấy course module
    const fallbackUrl = `${moodleBaseUrl}/mod/bigbluebuttonbn/bbb_view.php?action=join&id=${bigBlueButtonId}&bn=${bigBlueButtonId}&wstoken=${token}`;
    return fallbackUrl;
  }
}

/**
 * Get fallback URLs for users without course access
 */
export async function getBigBlueButtonFallbackUrls(
  bigBlueButtonId: number
): Promise<string[]> {
  const moodleBaseUrl = process.env.REACT_APP_MOODLE_API?.replace(
    "/webservice/rest/server.php",
    ""
  );

  if (!moodleBaseUrl) {
    throw new Error("Moodle base URL not configured");
  }

  // Get token for URLs
  const token =
    process.env.REACT_APP_MOODLE_TOKEN ||
    localStorage.getItem("moodle_token") ||
    "cf8d63a864797fed69f052f633c07d2e";

  // Attempt to find course module ID for proper join
  const cmid = await findCourseModuleIdForBBB(bigBlueButtonId);
  const urls: string[] = [];
  if (cmid) {
    urls.push(
      `${moodleBaseUrl}/mod/bigbluebuttonbn/bbb_view.php?action=join&id=${cmid}&bn=${bigBlueButtonId}&wstoken=${token}`
    );
    urls.push(
      `${moodleBaseUrl}/mod/bigbluebuttonbn/view.php?id=${cmid}&wstoken=${token}`
    );
  }
  // Fallback using BBB ID directly
  urls.push(
    `${moodleBaseUrl}/mod/bigbluebuttonbn/bbb_view.php?action=join&id=${bigBlueButtonId}&bn=${bigBlueButtonId}&wstoken=${token}`
  );
  urls.push(
    `${moodleBaseUrl}/mod/bigbluebuttonbn/view.php?b=${bigBlueButtonId}&wstoken=${token}`
  );

  return urls;
}

export async function getBigBlueButtonRecordings(bigbluebuttonbnId: number) {
  try {
    const { data } = await moodleInstanceApi.get(
      "/webservice/rest/server.php",
      {
        params: {
          wsfunction: "mod_bigbluebuttonbn_get_recordings",
          bigbluebuttonbnid: bigbluebuttonbnId,
        },
      }
    );
    return data;
  } catch (error) {
    return [];
  }
}

/**
 * Check if BigBlueButton meeting is running
 */
export async function isBigBlueButtonMeetingRunning(
  bigbluebuttonbnId: number
): Promise<boolean> {
  try {
    const meetingInfo = await getBigBlueButtonMeetingInfo(bigbluebuttonbnId);
    return (
      meetingInfo?.running === true || meetingInfo?.info?.running === "true"
    );
  } catch (error) {
    return false;
  }
}

export const viewBigBlueButton = async (bigbluebuttonbnid : any) => {
  try {
    const response = await moodleInstanceApi.get(
      "/webservice/rest/server.php",
      {
        params: {
          wsfunction: "mod_bigbluebuttonbn_view_bigbluebuttonbn",
          bigbluebuttonbnid: bigbluebuttonbnid,
        },
      }
    );
    return response?.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
}
